import { Input, type InputProps, type InputRef, type InputRef as AntInputRef } from "antd";
import type { TextAreaProps } from "antd/es/input";
import clsx from "clsx";
import { forwardRef, type ReactNode } from "react";

type BaseFieldProps = {
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  wrapperClassName?: string;
};

export type AppInputProps = InputProps &
  BaseFieldProps & {
    inputType?: "default" | "password" | "search";
    enterButton?: ReactNode;
    loading?: boolean;
    onSearch?: (value: string) => void;
  };

export type AppTextAreaProps = TextAreaProps & BaseFieldProps;

const FieldShell = ({
  label,
  helperText,
  errorText,
  className,
  children,
}: BaseFieldProps & {
  className?: string;
  children: ReactNode;
}) => {
  return (
    <label className={clsx("app-ant-field", className)}>
      {label && <span className="app-ant-field-label">{label}</span>}
      {children}
      {(errorText || helperText) && (
        <span className={clsx("app-ant-field-message", errorText && "app-ant-field-message-error")}>
          {errorText || helperText}
        </span>
      )}
    </label>
  );
};

export const AppInput = forwardRef<InputRef, AppInputProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      helperText,
      errorText,
      status,
      inputType = "default",
      enterButton,
      loading,
      onSearch,
      ...props
    },
    ref,
  ) => {
    const inputStatus = status || (errorText ? "error" : undefined);
    const sharedClassName = clsx("app-ant-input", className);

    return (
      <FieldShell label={label} helperText={helperText} errorText={errorText} className={wrapperClassName}>
        {inputType === "password" && (
          <Input.Password
            ref={ref as React.Ref<AntInputRef>}
            className={sharedClassName}
            status={inputStatus}
            {...props}
          />
        )}
        {inputType === "search" && (
          <Input.Search
            ref={ref as React.Ref<AntInputRef>}
            className={sharedClassName}
            status={inputStatus}
            enterButton={enterButton}
            loading={loading}
            onSearch={onSearch}
            {...props}
          />
        )}
        {inputType === "default" && (
          <Input ref={ref} className={sharedClassName} status={inputStatus} {...props} />
        )}
      </FieldShell>
    );
  },
);

AppInput.displayName = "AppInput";

export const AppTextArea = forwardRef<HTMLTextAreaElement, AppTextAreaProps>(
  ({ className, wrapperClassName, label, helperText, errorText, status, ...props }, ref) => {
    return (
      <FieldShell label={label} helperText={helperText} errorText={errorText} className={wrapperClassName}>
        <Input.TextArea
          ref={ref}
          className={clsx("app-ant-input app-ant-textarea", className)}
          status={status || (errorText ? "error" : undefined)}
          {...props}
        />
      </FieldShell>
    );
  },
);

AppTextArea.displayName = "AppTextArea";
